import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Copy } from 'react-feather';
import { Alert, Badge, Button, Card, Col, ListGroup, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  connectWhatsAppAPI,
  createWhatsAppTemplateAPI,
  getWhatsappSetupAPI,
  sendTestMessageAPI,
  verifyWebhookAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { companyUrl } from '../../../utils/mhNav';
import styles from './WhatsAppSetup.module.scss';

export default function WhatsAppSetup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testOpen, setTestOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWhatsappSetupAPI();
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyUrl = () => {
    navigator.clipboard.writeText(data.webhookUrl);
    toast.success('Copied');
  };

  if (loading && !data) {
    return (
      <div>
        <PageHeader title="WhatsApp" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations', path: companyUrl('integrations') }, { label: 'WhatsApp' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="WhatsApp" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Integrations', path: companyUrl('integrations') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  const whBlock = data.blocks?.find((b) => b.id === 'wh');

  return (
    <div>
      <PageHeader
        title="WhatsApp Business setup"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Integrations', path: companyUrl('integrations') },
          { label: 'WhatsApp' },
        ]}
        secondaryActions={[
          {
            label: 'Verify webhook',
            onClick: async () => {
              try {
                await verifyWebhookAPI();
                toast.success('Webhook verified');
                load();
              } catch (e) {
                toast.error(e.message);
              }
            },
            variant: 'outline-primary',
          },
          { label: 'Send test message', onClick: () => setTestOpen(true), variant: 'outline-secondary' },
        ]}
      />

      <Row className="g-3 mb-4">
        {(data.blocks || []).map((b) => (
          <Col md={6} lg={3} key={b.id}>
            <div className={styles.statusRow}>
              <h3 className="h6">{b.title}</h3>
              <Badge bg={b.status === 'ok' ? 'success' : b.status === 'error' ? 'danger' : 'secondary'}>{b.status}</Badge>
              <p className="small text-muted mt-2 mb-2">{b.detail}</p>
              <Button size="sm" variant="outline-primary">
                {b.action}
              </Button>
            </div>
          </Col>
        ))}
      </Row>

      {whBlock?.status === 'error' && (
        <Alert variant="danger" className="small">
          Webhook error — use Verify until the chip turns green.
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header>Checklist</Card.Header>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <strong>1.</strong> Connect Meta{' '}
            <Button
              size="sm"
              className="ms-2"
              onClick={async () => {
                try {
                  await connectWhatsAppAPI();
                  toast.success('Connected (mock)');
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Connect
            </Button>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>2.</strong> Verify webhook
            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
              <code className="small flex-grow-1 text-break">{data.webhookUrl}</code>
              <Button size="sm" variant="outline-secondary" onClick={copyUrl}>
                <Copy size={14} />
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await verifyWebhookAPI();
                    toast.success('Verified');
                    load();
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
              >
                Verify
              </Button>
            </div>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>3.</strong> Templates
            <ul className="small mb-2 mt-1">
              {(data.templates || []).map((t) => (
                <li key={t.id}>
                  {t.name} — {t.status}
                </li>
              ))}
            </ul>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={async () => {
                try {
                  await createWhatsAppTemplateAPI({ name: `custom_${Date.now()}` });
                  toast.success('Template submitted');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Create template
            </Button>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>4.</strong> Test — use &quot;Send test message&quot; in the header
          </ListGroup.Item>
        </ListGroup>
      </Card>

      <ConfirmModal
        isOpen={testOpen}
        onClose={() => setTestOpen(false)}
        onConfirm={async () => {
          try {
            await sendTestMessageAPI('+96870001234');
            toast.success('Test message sent (mock)');
            setTestOpen(false);
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Send test message?"
        message="Preview: +968 7000 1234 — MeetingHub test"
        confirmLabel="Send"
      />
    </div>
  );
}
